import { createElement, MouseEvent, useEffect, useState, KeyboardEvent, forwardRef } from 'react';
import { SwitchOn, SwitchOff } from '@sas/react-svg';
import { postfixTestId } from '../../util/testId';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { Icon } from '../Icon';
import type { SwitchProps } from './types';
import { markEvent } from '../../marked-events';

import bundle from './NovaComponents-Switch-gui-icu.properties';

import cx from 'classnames';
import cn from './Switch.css.json';
import './Switch.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    style,
    className,
    isOn,
    defaultOn = false,
    disabled = false,
    readOnly = false,
    tooltip = {},
    testId,
    onPress,
    ariaLabel,
    ariaDescribedBy = [],
    ariaLabelledBy,
  },
  ref
) {
  const [switchStatus, setSwitchStatus] = useState(defaultOn);
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const isControlled = isOn !== undefined;

  useEffect(() => {
    if (isControlled) {
      setSwitchStatus(isOn ?? false);
    }
  }, [isControlled, isOn]);

  const handleSize = '0.5rem';
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars(
      {
        ...(switchStatus
          ? dt.stateSecondaryToggleNova.selected
          : dt.stateSecondaryToggleNova.normal),
        'anim-tf': dt.animation.timingFunction.standard,
        'anim-dur': dt.animation.duration.standard,
        'handle-size': handleSize,
      },
      'switch'
    ),
    ...renderInlineCssVars(
      switchStatus
        ? dt.stateSecondaryToggleNova.disabledSelected
        : dt.stateSecondaryToggleNova.disabled,
      'switch-disabled'
    ),
  };

  const onSwitchChange = (
    newState: boolean,
    event: MouseEvent | KeyboardEvent<HTMLButtonElement>
  ) => {
    if (!isControlled) {
      setSwitchStatus(newState);
    }

    onPress?.(newState, event);
  };

  return (
    <div
      data-testid={testId}
      style={{ ...tokens, ...style }}
      className={cx(cn.container, className)}
    >
      <button
        ref={ref}
        type="button"
        data-testid={postfixTestId(testId, 'button')}
        className={cx(cn.switchButton, disabled && cn.disabled, readOnly && cn.readonly)}
        role="switch"
        aria-checked={switchStatus}
        aria-disabled={disabled ? true : undefined}
        aria-readonly={readOnly && !disabled ? true : undefined}
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined}
        title={
          switchStatus
            ? tooltip.on ?? localize('on.tooltip.label')
            : tooltip.off ?? localize('off.tooltip.label')
        }
        tabIndex={0}
        onMouseUp={(evt) => {
          if (disabled || readOnly) return;
          evt.preventDefault();
          onSwitchChange(!switchStatus, evt);
        }}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            // Mark the event so Dialog doesn't submit a default action
            markEvent(evt);
          }
        }}
        onKeyUp={(evt) => {
          if (disabled || readOnly) return;
          switch (evt.key) {
            case ' ':
            case 'Enter':
              evt.preventDefault();
              onSwitchChange(!switchStatus, evt);
              return;
            case 'ArrowRight':
              onSwitchChange(isRTL ? false : true, evt);
              return;
            case 'ArrowLeft':
              onSwitchChange(isRTL ? true : false, evt);
              return;
          }
        }}
      >
        <div
          className={cn.switchInner}
          style={{
            transform: `${
              switchStatus
                ? isRTL
                  ? 'translate3d(calc(-100% - 1px + 1.5 * var(--switch-handle-size)), 0, 0)'
                  : 'translate3d(calc(100% + 1px - 1.5 * var(--switch-handle-size)), 0, 0)'
                : 'translate3d(0, 0, 0)'
            }${isRTL ? ' rotateX(180deg)' : ''}`,
          }}
        >
          <div className={cn.handle}>
            <Icon
              icon={SwitchOn}
              className={cn.iconOn}
              tokens={() =>
                disabled ? { fg: 'var(--switch-disabled-fg)' } : { fg: 'var(--switch-fg)' }
              }
              tooltip=""
              ariaLabel=""
            />
            <Icon
              icon={SwitchOff}
              className={cn.iconOff}
              tokens={() =>
                disabled ? { fg: 'var(--switch-disabled-fg)' } : { fg: 'var(--switch-fg)' }
              }
              tooltip=""
              ariaLabel=""
            />
          </div>
        </div>
      </button>
    </div>
  );
});
